import {
  Activity,
  BarChart3,
  BookOpen,
  CalendarClock,
  CalendarRange,
  CheckCircle,
  ClipboardCheck,
  Eye,
  FileText,
  Home,
  KeyRound,
  LayoutDashboard,
  Library,
  Link2,
  MailCheck,
  MessageSquare,
  PackageCheck,
  Palette,
  RefreshCw,
  ScrollText,
  Send,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  UserCog,
  UserSearch,
  Waypoints,
  Wrench,
  UsersRound,
} from "lucide-react";

/**
 * Single source of truth for every admin destination.
 *
 * The sidebar, the landing page and `navigation.test.js` all read this list, so
 * a page reaches the navigation by being declared here once rather than by
 * being hand-added to a component. The test asserts parity with the route table
 * in `App.jsx`, which is what makes an unreachable page a failing build instead
 * of something a reviewer has to notice.
 *
 * Entries are ordered by group, and each group renders in first-appearance
 * order. Groups are derived from what a page does, not its history: the order
 * below is the order the rail shows.
 *
 * @typedef {object} NavEntry
 * @property {string}   id          Stable key, independent of the route so a URL can change without breaking references.
 * @property {string}   route       The path this entry owns.
 * @property {string}   label       Visible title. This is the information-scent surface — it should let someone predict the page.
 * @property {string}   description One line of what the page is for. Shown on the homepage card; keep it a single sentence.
 * @property {Function} icon        lucide-react component. Unique across the manifest; the test enforces it.
 * @property {?string}  group       Group heading, or null for the ungrouped block pinned above the scroll region.
 * @property {(ctx: NavContext) => boolean} visible  Display predicate. NOT an access control — the route guard is.
 * @property {string}  [subPageOf]  id of the entry that stays highlighted while this page is open.
 * @property {true}    [hidden]     Reachable, but deliberately absent from the rail. Still parity-checked.
 *
 * @typedef {object} NavContext
 * @property {boolean} isStaff  Dashmote staff address, excluding demo accounts.
 * @property {boolean} isAdmin  Superadmin, per useAdminStatus().
 */

/** Everything in the portal is staff-only; Super Admin narrows further. */
const staffOnly = (ctx) => ctx.isStaff;
const adminOnly = (ctx) => ctx.isAdmin;

/** @type {NavEntry[]} */
export const NAV_ENTRIES = [
  // Home — the landing page. A null group pins it above the scrolling,
  // collapsible topic sections, so getting back is one labelled click rather
  // than a hidden logo tap.
  {
    id: "home",
    route: "/",
    label: "Home",
    description: "The admin portal landing page.",
    icon: Home,
    group: null,
    visible: staffOnly,
  },

  // Workspaces & Users — provisioning workspaces, administering their users,
  // and the governance log.
  {
    id: "internal-configuration",
    route: "/internal-configuration",
    label: "Workspace Configuration",
    description: "Configure the inside of a workspace — core brands, competitors, and scoring weights. The internal counterpart of the client Settings page.",
    icon: SlidersHorizontal,
    group: "Workspaces & Users",
    visible: staffOnly,
  },
  {
    // Reached from the "Manage User Scope" quick action on the homepage, not
    // the rail — so it's hidden, and its breadcrumb roots at Home. No
    // `subPageOf`: it no longer lives under Workspace Configuration, so nothing
    // in the rail should highlight when it's open.
    id: "user-scope-management",
    route: "/user-scope-management",
    label: "User Scope Management",
    description: "Edit a user's product access, account type, and admin rights.",
    icon: ShieldCheck,
    group: "Workspaces & Users",
    visible: staffOnly,
    hidden: true,
  },
  {
    id: "api-keys",
    route: "/api-keys",
    label: "API Keys",
    description: "Create and revoke client-scoped API keys for programmatic access.",
    icon: KeyRound,
    group: "Workspaces & Users",
    visible: staffOnly,
  },
  {
    id: "invite-links",
    route: "/invite-links",
    label: "Invite Links",
    description: "View and revoke a workspace's group invite links.",
    icon: Link2,
    group: "Workspaces & Users",
    visible: staffOnly,
  },
  {
    id: "audit-trail",
    route: "/audit-trail",
    label: "Audit Trail",
    description: "Search the staff audit log by event type, email, or UID.",
    icon: ScrollText,
    group: "Workspaces & Users",
    visible: staffOnly,
  },
  {
    id: "view-as-user",
    // Was also Eye, which made the icon column meaningless next to Workflow
    // Visibility.
    icon: UserSearch,
    route: "/view-as-user",
    label: "View as User",
    description: "Open DashONE as a chosen user sees it, for support.",
    group: "Workspaces & Users",
    visible: staffOnly,
  },

  // Product Analytics — how the product is used, and how users feel about it.
  // Second by intent: it is the daily-tracked section.
  {
    id: "usage",
    route: "/dashboard",
    label: "Dashboard",
    description: "See platform usage — chats, workflow runs, and key metrics.",
    icon: LayoutDashboard,
    group: "Product Analytics",
    visible: staffOnly,
  },
  {
    id: "page-analytics",
    route: "/page-analytics",
    label: "Page Analytics",
    description: "Analyse product adoption, engagement, and retention by page.",
    icon: Activity,
    group: "Product Analytics",
    visible: staffOnly,
  },
  {
    id: "user-analytics",
    route: "/user-analytics",
    label: "User Analytics",
    description: "Review per-user activity, engagement, and feature usage.",
    icon: BarChart3,
    group: "Product Analytics",
    visible: staffOnly,
  },
  {
    id: "feedback",
    route: "/feedback",
    label: "Feedback & Sentiment",
    description: "Read user feedback and sentiment trends.",
    icon: MessageSquare,
    group: "Product Analytics",
    visible: staffOnly,
  },

  // Data Delivery & Quality — the pipeline that produces client data and the
  // checks on it.
  {
    id: "delivered-features",
    route: "/delivered-features",
    label: "Delivered Features",
    description: "Review standard versus customized features delivered to each client.",
    icon: PackageCheck,
    group: "Data Delivery & Quality",
    visible: staffOnly,
  },
  {
    id: "refresh-monitor",
    route: "/refresh-monitor",
    label: "Refresh Monitor",
    description: "Spot ClickHouse refresh problems per workspace and market.",
    icon: RefreshCw,
    group: "Data Delivery & Quality",
    visible: staffOnly,
  },
  {
    id: "qa-dashboard",
    route: "/qa-dashboard",
    label: "QA Dashboard",
    description: "Track data-quality checks and KPIs across refreshes.",
    icon: ClipboardCheck,
    group: "Data Delivery & Quality",
    visible: staffOnly,
  },

  {
    // The feed function needs the dashmote_admin flag, so the entry does too.
    id: "key-accounts-daily",
    route: "/key-accounts-daily",
    label: "Key Accounts Daily",
    description: "Edit each Daily feed's crawl, SKUs, key accounts and promo calendar.",
    icon: CalendarRange,
    group: "Data Delivery & Quality",
    visible: adminOnly,
  },
  // Brands (CASS) — the brand taxonomy and the workspace→client identity graph.
  {
    id: "cass-catalog",
    route: "/cass-catalog",
    label: "Catalog",
    description: "Browse the CASS taxonomy — companies, brands, subbrands, platforms.",
    icon: Library,
    group: "Brands (CASS)",
    visible: staffOnly,
  },
  {
    id: "cass-management",
    route: "/cass-management",
    label: "Management",
    description: "Edit a CASS account's brands and mappings for a market set.",
    icon: Wrench,
    group: "Brands (CASS)",
    visible: staffOnly,
  },
  {
    id: "cass-mappings",
    route: "/cass-mappings",
    label: "Mappings",
    description: "See which CASS account, DWD client, and country each workspace runs on.",
    icon: Waypoints,
    group: "Brands (CASS)",
    visible: staffOnly,
  },

  // Reports & Email — the report-and-email lifecycle: schedule, generate,
  // deliver, campaign, and per-user report settings. Reports are sent by email,
  // so report jobs and user report settings live here rather than under Data
  // Delivery or Workspaces & Users.
  {
    id: "email-scheduling",
    route: "/email-scheduling",
    label: "Email Scheduling",
    description: "Manage scheduled and recurring client emails.",
    icon: CalendarClock,
    group: "Reports & Email",
    visible: staffOnly,
  },
  {
    id: "reports-jobs",
    route: "/reports-jobs",
    label: "Monthly Reports",
    description: "Browse generated monthly performance reports.",
    icon: FileText,
    group: "Reports & Email",
    visible: staffOnly,
  },
  {
    id: "email-delivery-log",
    route: "/email-delivery-log",
    label: "Email Delivery Log",
    description: "Check whether invitation and password emails were delivered.",
    icon: MailCheck,
    group: "Reports & Email",
    visible: staffOnly,
  },
  {
    id: "email-campaigns",
    route: "/email-campaigns",
    label: "Email Campaigns",
    description: "Compose and send targeted email campaigns to user groups.",
    icon: Send,
    group: "Reports & Email",
    visible: staffOnly,
  },
  {
    id: "user-config",
    route: "/user-config",
    label: "User Report Settings",
    description: "Manage a user's email and report preferences, plus activity and agent scope.",
    icon: UserCog,
    group: "Reports & Email",
    visible: staffOnly,
  },

  // AI Configuration — tuning how the AI product behaves and what it outputs,
  // plus the LLM provider setup that powers it.
  {
    id: "workflow-visibility",
    route: "/workflow-visibility",
    label: "Workflow Visibility",
    description: "Set per-client workflow and agent visibility overrides.",
    icon: Eye,
    group: "AI Configuration",
    visible: staffOnly,
  },
  {
    id: "deck-styles",
    route: "/deck-styles",
    label: "Deck Styles",
    description: "Configure PPTX and PDF export branding per workspace.",
    icon: Palette,
    group: "AI Configuration",
    visible: staffOnly,
  },
  {
    id: "cswap",
    route: "/cswap",
    label: "Account Usage",
    description: "Check shared Claude and Codex usage and reconnect accounts.",
    icon: UsersRound,
    group: "AI Configuration",
    visible: staffOnly,
  },
  {
    id: "content-quality",
    route: "/content-quality",
    label: "Content Quality",
    description: "Monitor AI feature reliability and content-generation quality.",
    icon: CheckCircle,
    group: "AI Configuration",
    visible: staffOnly,
  },
  {
    id: "learning-rules",
    route: "/learning-rules",
    label: "Learning Rules",
    description: "Review AI correction rules derived from user feedback.",
    icon: BookOpen,
    group: "AI Configuration",
    visible: staffOnly,
  },
  {
    // Admin-only LLM provider and model configuration. Client API keys live on
    // the separate API Keys page under Workspaces & Users.
    id: "superadmin",
    route: "/superadmin",
    label: "LLM Providers",
    description: "Configure LLM providers and models.",
    icon: ShieldAlert,
    group: "AI Configuration",
    visible: adminOnly,
  },
];

/**
 * Routes that exist in the route table but own no nav entry, each with the
 * reason. Keeping them here rather than in the test means the parity check has
 * one place to consult and every exemption carries its justification.
 * @internal
 */
export const UNLISTED_ROUTES = {
  "/login": "Pre-auth; the rail does not exist yet.",
  "/*": "Router wrapper around the protected route tree, not a destination.",
  "*": "Catch-all. Redirects a renamed route to its current home, anything else to the landing page.",
};

/**
 * Old path -> current path, for routes renamed after links were already in
 * circulation.
 *
 * The catch-all is the backstop beneath this map, not a substitute: without an
 * entry here a stale link still resolves, but to the landing page rather than to
 * the page it meant. `navigation.test.js` asserts every value is a live manifest
 * route, which is also what forecloses an off-site redirect.
 */
export const ROUTE_REDIRECTS = {
  "/internal_configuration": "/internal-configuration",
  "/user_scope_management": "/user-scope-management",
  "/api_keys": "/api-keys",
  "/audit_trail": "/audit-trail",
  "/cass_catalog": "/cass-catalog",
  "/cass_management": "/cass-management",
  "/cass_mappings": "/cass-mappings",
  "/delivered_features": "/delivered-features",
  "/refresh_monitor": "/refresh-monitor",
  "/qa_dashboard": "/qa-dashboard",
  "/email_delivery_log": "/email-delivery-log",
  "/usage": "/dashboard",
};

/**
 * The dashai (shadcn) admin pages, rendered inside the AdminShell frame and
 * hard-gated on `isAdmin` (a non-admin is redirected away). The manifest treats
 * them as admin-only so the rail and palette match that gate: staff who are not
 * admins no longer see rows that would only bounce them. Two consumers read this
 * set — `entryVisible` (rail visibility) and `isAdminShellRoute` (asserted by
 * the parity test). This set MUST equal AdminShell's child routes in App.jsx.
 */
const ADMIN_SHELL_ROUTES = new Set([
  "/dashboard",
  "/user-analytics",
  "/page-analytics",
  "/user-config",
  "/email-campaigns",
  "/reports-jobs",
  "/feedback",
  "/content-quality",
  "/learning-rules",
  "/workflow-visibility",
  "/deck-styles",
  "/view-as-user",
  "/email-scheduling",
]);

/**
 * Whether a path is a dashai (shadcn) page rendered in the AdminShell frame, as
 * opposed to an Ant page under GeneralLayout. Used by the parity test to assert
 * ADMIN_SHELL_ROUTES stays in lockstep with App.jsx's AdminShell child routes.
 * @internal
 */
export const isAdminShellRoute = (pathname) => ADMIN_SHELL_ROUTES.has(pathname);

/**
 * Whether the user may see this entry. The entry's own `visible` predicate,
 * narrowed by the AdminShell gate: an AdminShell route additionally requires
 * `isAdmin`, so the rail never shows a staff-non-admin a page that redirects.
 */
const entryVisible = (entry, ctx) =>
  entry.visible(ctx) && (!ADMIN_SHELL_ROUTES.has(entry.route) || ctx.isAdmin);

/** Entries the given user may see, in manifest order. */
export const visibleEntries = (ctx) =>
  NAV_ENTRIES.filter((entry) => entryVisible(entry, ctx));

/** Entries the rail renders — visible, minus the ones reached from elsewhere. */
export const railEntries = (ctx) =>
  visibleEntries(ctx).filter((entry) => !entry.hidden);

/** Bucket entries by group, in first-appearance order. */
const groupInOrder = (entries) => {
  const groups = [];
  for (const entry of entries) {
    let group = groups.find((candidate) => candidate.name === entry.group);
    if (!group) {
      group = { name: entry.group, entries: [] };
      groups.push(group);
    }
    group.entries.push(entry);
  }
  return groups;
};

/**
 * Groups for rendering the rail, in first-appearance order. A `null` group,
 * when any entry declares one, is preserved as its own leading bucket so the
 * Sidebar can pin it above the scroll region.
 */
export const railGroups = (ctx) => groupInOrder(railEntries(ctx));

/**
 * Groups for the command palette. Unlike the rail, this includes `hidden`
 * entries (e.g. User Scope Management), so every reachable page is findable by
 * name even when it is not shown in the rail.
 */
export const paletteGroups = (ctx) => groupInOrder(visibleEntries(ctx));

/**
 * The entry that should read as active for a path.
 *
 * Matching respects segment boundaries: `/usage-report` is not a match for
 * `/usage`. A bare `startsWith` would let any entry claim an unrelated path
 * that merely shares its stem, and sorting by length does not help — that only
 * disambiguates between two declared routes, never between a declared route and
 * an undeclared path built on it.
 *
 * Among genuine matches the longest wins, so a nested path resolves to its
 * deepest declared parent. A `subPageOf` entry resolves to its parent, which is
 * what keeps the rail highlighted on pages opened from another page rather than
 * from the rail.
 */
/**
 * The manifest entry a path lands on — the deepest declared match, before any
 * `subPageOf` resolution. This is the page you are actually on (so a breadcrumb
 * can name it), where `activeEntryFor` instead resolves to the rail parent.
 */
export const entryForRoute = (pathname) =>
  NAV_ENTRIES.filter(
    (entry) => pathname === entry.route || pathname.startsWith(`${entry.route}/`),
  ).sort((a, b) => b.route.length - a.route.length)[0] ?? null;

export const activeEntryFor = (pathname) => {
  const match = entryForRoute(pathname);
  if (!match) return null;
  if (!match.subPageOf) return match;
  return NAV_ENTRIES.find((entry) => entry.id === match.subPageOf) ?? match;
};

